import React, { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Table,
  Card,
  Select,
  DatePicker,
  Button,
  Space,
  Input,
  Form,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import Pageloader from "../../components/loader";
import { getWalletComplianceHistory } from "../../services/walletCompliance.service";
import { validationErrors } from "../../utils/Helpers";
import * as zoneActions from "../../redux/actions/zone.actions";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const pageName = "Wallet Compliance History";

const numberFormatter = new Intl.NumberFormat();

const formatNumber = (value) => {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return numberFormatter.format(num);
};

const HOLD_REMOVED_BY_LABELS = {
  rider: "Rider",
  help_center: "Back Office",
  api: "Help Center",
};

const ENFORCEMENT_OPTIONS = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const formatHoldRemovedBy = (value) => {
  if (!value) return "-";
  if (typeof value === "object") {
    const userId = value.userId ?? "-";
    const username = value.username ?? "-";
    const email = value.email ?? "-";
    return `${userId} / ${username} / ${email}`;
  }
  return HOLD_REMOVED_BY_LABELS[value] || value;
};

const WalletComplianceHistory = (props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalCount: 0,
  });
  const [zoneOptions, setZoneOptions] = useState([]);
  const [allZoneIds, setAllZoneIds] = useState([]);
  const [filters, setFilters] = useState({
    zoneId: [],
    displayId: "",
    enforcement: undefined,
    date: [moment().startOf("day"), moment().endOf("day")],
  });

  useEffect(() => {
    props.zoneActions.getZoneListAction();
  }, []);

  useEffect(() => {
    if (props.zoneData) {
      const options = [];
      const ids = [];
      props.zoneData.forEach((item) => {
        const zoneId = item?.id ? String(item.id) : null;
        if (!zoneId) return;
        options.push({ value: zoneId, label: item?.zoneName || zoneId });
        ids.push(zoneId);
      });
      setAllZoneIds(ids);
      setZoneOptions([{ value: "all", label: "---SELECT ALL---" }, ...options]);
    }
  }, [props.zoneData]);

  const disabledDate = (current) => {
    if (!current) return false;
    const minDate = moment().subtract(3, "months").startOf("day");
    const maxDate = moment().endOf("day");
    return current.isBefore(minDate) || current.isAfter(maxDate);
  };

  const onChangeZone = (value) => {
    if (value?.includes("all")) {
      form.setFieldsValue({ zoneId: allZoneIds });
    }
  };

  const fetchList = useCallback(
    async (page = 1, limit = 10, nextFilters = filters) => {
      try {
        setLoading(true);
        const dateRange = nextFilters.date || [
          moment().startOf("day"),
          moment().endOf("day"),
        ];
        const params = {
          page,
          limit,
          startDate: dateRange[0]?.startOf("day")?.toISOString(),
          endDate: dateRange[1]?.endOf("day")?.toISOString(),
        };
        if (nextFilters.displayId) {
          params.displayId = String(nextFilters.displayId).trim();
        }
        if (nextFilters.enforcement) {
          params.enforcement = nextFilters.enforcement;
        }
        if (Array.isArray(nextFilters.zoneId) && nextFilters.zoneId.length) {
          params.zoneId = nextFilters.zoneId.join(",");
        }
        const { data } = await getWalletComplianceHistory(params);
        setRows(data?.data || []);
        setPagination(
          data?.pagination || {
            currentPage: page,
            limit,
            totalCount: 0,
          }
        );
      } catch (e) {
        validationErrors(e);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchList(1, pagination.limit, filters);
  }, []);

  const onSearch = (values) => {
    const nextFilters = {
      zoneId: values.zoneId || [],
      displayId: values.displayId || "",
      enforcement: values.enforcement || undefined,
      date: values.date || [moment().startOf("day"), moment().endOf("day")],
    };
    setFilters(nextFilters);
    fetchList(1, pagination.limit, nextFilters);
  };

  const onReset = () => {
    const nextFilters = {
      zoneId: [],
      displayId: "",
      enforcement: undefined,
      date: [moment().startOf("day"), moment().endOf("day")],
    };
    form.setFieldsValue({
      ...nextFilters,
      enforcement: undefined,
    });
    setFilters(nextFilters);
    fetchList(1, pagination.limit, nextFilters);
  };

  const columns = [
    {
      title: "Rider ID",
      dataIndex: "display_id",
      render: (v) => v || "-",
    },
    {
      title: "Rider Name",
      dataIndex: "rider_name",
      render: (v) => v || "-",
    },
    {
      title: "Zone",
      dataIndex: "zone_name",
      render: (v) => v || "-",
    },
    {
      title: "Wallet Balance",
      dataIndex: "wallet_balance",
      render: (v) => formatNumber(v),
    },
    {
      title: "Wallet Limit",
      dataIndex: "wallet_limit",
      render: (v) => formatNumber(v),
    },
    {
      title: "Order Value",
      dataIndex: "order_value",
      render: (v) => formatNumber(v),
    },
    {
      title: "Violation #",
      dataIndex: "violation_count",
      render: (v) => (v != null ? v : "-"),
    },
    {
      title: "Enforcement",
      dataIndex: "enforcement_triggered",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Rider Status",
      dataIndex: "rider_status",
      render: (v) => v || "-",
    },
    {
      title: "Break Reason",
      dataIndex: "break_reason",
      render: (v) => v || "-",
    },
    {
      title: "Hold Removed By",
      dataIndex: "hold_removed_by",
      render: (v) => formatHoldRemovedBy(v),
    },
    {
      title: "Hold Removed At",
      dataIndex: "hold_removed_at",
      render: (v) => (v ? moment(v).format("DD MMM YYYY h:mm A") : "-"),
    },
    {
      title: "Violation Time",
      dataIndex: "createdAt",
      render: (v) => (v ? moment(v).format("DD MMM YYYY h:mm A") : "-"),
    },
  ];

  return (
    <div className="page-content">
      <div className="page-content-header">
        <div className="pch">
          <Title level={2}>{pageName}</Title>
        </div>
      </div>
      <div className="p-content">
        <Card>
          <Form
            form={form}
            className="fliters_form no-label"
            layout="vertical"
            onFinish={onSearch}
            initialValues={filters}
          >
            <Space wrap align="start">
              <Form.Item name="zoneId" label="Zone">
                <Select
                  showSearch
                  allowClear
                  mode="multiple"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={zoneOptions}
                  onChange={onChangeZone}
                  style={{ minWidth: 300 }}
                  placeholder="Zone"
                />
              </Form.Item>
              <Form.Item name="displayId" label="Rider ID">
                <Input
                  allowClear
                  style={{ width: 140 }}
                  placeholder="Rider ID"
                />
              </Form.Item>
              <Form.Item name="enforcement" label="Enforcement">
                <Select
                  allowClear
                  options={ENFORCEMENT_OPTIONS}
                  style={{ minWidth: 120 }}
                  placeholder="Enforcement"
                />
              </Form.Item>
              <Form.Item name="date" label="Date">
                <RangePicker
                  allowClear={false}
                  format="YYYY-MM-DD"
                  disabledDate={disabledDate}
                />
              </Form.Item>
              <Form.Item label=" ">
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button loading={loading} onClick={onReset}>
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </Space>
          </Form>

          {loading ? (
            <Pageloader />
          ) : (
            <Table
              rowKey={(record) => record._id}
              dataSource={rows}
              columns={columns}
              scroll={{ x: 1400 }}
              pagination={{
                total: pagination.totalCount,
                current: pagination.currentPage,
                pageSize: pagination.limit,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total}`,
                onChange: (page, pageSize) =>
                  fetchList(page, pageSize, filters),
              }}
              locale={{ emptyText: <Text>No compliance history found.</Text> }}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    zoneData: state.ZoneReducer.zoneData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    zoneActions: bindActionCreators(zoneActions, dispatch),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WalletComplianceHistory);
